import React from 'react'
import {
  Box,
  Button,
  Checkbox,
  Field,
  Flex,
  Heading,
  Input,
  Message,
  Modal,
  toast,
} from '@workwhile/ui'
import { useForm, Controller } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { editRateSchema, EditRateSchema } from '../schemas/editRate'
import { useEditRateFlowContextValue } from '../context/EditRateFlowProvider'
import { useRuleDetailContextValue } from '../../../context/RuleDetailProvider'
import { useRuleDetailWorkflowsContextValue } from '../../../context/RuleDetailWorkflowsProvider'

export const EditRateFlowMain = () => {
  const {
    state: { id: ruleId },
  } = useRuleDetailContextValue()

  const {
    editRateFlow: { isOpen, close, selectedRate },
  } = useRuleDetailWorkflowsContextValue()

  const {
    state: {
      mutationState: { isPending, errorMessage },
    },
    actions: { submit },
  } = useEditRateFlowContextValue()

  const {
    handleSubmit,
    register,
    control,
    formState: { errors },
    reset,
  } = useForm<EditRateSchema>({
    resolver: zodResolver(editRateSchema),
    defaultValues: {
      hourlyRateDollars: selectedRate
        ? selectedRate.hourlyRateCents / 100
        : undefined,
      note: selectedRate?.note ?? '',
      isEnabled: selectedRate?.isEnabled ?? true,
    },
  })

  if (!selectedRate) return null

  const onSubmit = handleSubmit((data) => {
    submit(
      {
        ruleId,
        rateId: selectedRate.id,
        hourlyRateCents: Math.round(data.hourlyRateDollars * 100),
        note: data.note,
        isEnabled: data.isEnabled,
      },
      {
        onSuccess: () => {
          toast.success('Rate updated successfully', { position: 'top-center' })
          close()
        },
      }
    )
  })

  const handleClose = () => {
    reset()
    close()
  }

  return (
    <Modal
      key={selectedRate?.id ?? 'edit-rate'}
      open={isOpen}
      onClose={handleClose}
      position="top"
      loading={isPending}
      customCta={
        <Flex>
          <Button variant="text" onClick={handleClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={onSubmit}>
            Save
          </Button>
        </Flex>
      }
    >
      <Box
        as="form"
        onSubmit={(e: React.FormEvent) => {
          e.preventDefault()
          onSubmit()
        }}
      >
        <Heading level={3} fontWeight="400" style={{ margin: 0 }}>
          Edit rate
        </Heading>
        <Box mt={3}>
          <Field
            label="Hourly rate ($/hr)"
            error={errors.hourlyRateDollars?.message}
          >
            <Input
              {...register('hourlyRateDollars', { valueAsNumber: true })}
              type="number"
              step="0.01"
            />
          </Field>
        </Box>
        <Box mt={3}>
          <Field label="Note" error={errors.note?.message}>
            <Input {...register('note')} placeholder="Optional" />
          </Field>
        </Box>
        <Box mt={3}>
          <Controller
            name="isEnabled"
            control={control}
            render={({ field }) => (
              <Checkbox value={field.value} onChange={field.onChange}>
                Enabled
              </Checkbox>
            )}
          />
        </Box>
        {errorMessage ? (
          <Message variant="error" mt={3} borderRadius="small">
            {errorMessage}
          </Message>
        ) : null}
      </Box>
    </Modal>
  )
}
